import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { LOCALES, type Locale } from '@mrigl/shared';
import { Alert, Button, Card, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useEffect, useState, type SubmitEvent } from 'react';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import { useI18n } from '../i18n.js';

type Content = Schemas['LandingContent'];
type Localized = Partial<Record<Locale, string>>;

/**
 * The public landing page's own words (ADR 0027): features, questions and legal links, written
 * here per language. mrigl adds no wording of its own; an empty item is not shown.
 */
export function LandingContentPage() {
  const { t, locale } = useI18n();
  const [content, setContent] = useState<Content | null>(null);
  const [version, setVersion] = useState(0);
  const [language, setLanguage] = useState<Locale>(locale);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void unwrap(api.GET('/operator-api/v1/landing'))
      .then((state) => {
        setContent(state.content);
        setVersion(state.version);
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [t]);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (content === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await unwrap(
        api.PUT('/operator-api/v1/landing', { body: { version, content } }),
      );
      setContent(saved.content);
      setVersion(saved.version);
      setNotice(t('admin.landing.saved'));
    } catch (failure) {
      setError(
        failure instanceof ApiError && failure.code === 'validation-failed'
          ? t('admin.landing.invalid')
          : errorMessage(t, failure),
      );
    } finally {
      setBusy(false);
    }
  }

  if (content === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const set = (value: Localized, text: string): Localized => ({ ...value, [language]: text });
  const languageName = t(`admin.language.${language}`);
  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.landing.title')}
      onSubmit={(e) => void save(e)}
    >
      <div className="admin-pagehead">
        <div>
          <h1 className="mrigl-heading">{t('admin.landing.title')}</h1>
          <p className="mrigl-muted">{t('admin.landing.intro')}</p>
        </div>
        <SelectField
          label={t('admin.landing.language')}
          value={language}
          onChange={(event) => {
            setLanguage(event.target.value as Locale);
          }}
          options={LOCALES.map((value) => ({ value, label: t(`admin.language.${value}`) }))}
        />
      </div>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}

      <Card labelledBy="landing-features">
        <h2 id="landing-features" className="mrigl-subheading">
          {t('admin.landing.features')}
        </h2>
        {content.features.map((feature, index) => (
          <fieldset key={`feature-${String(index)}`} className="mrigl-stack admin-landing__item">
            <legend>{t('admin.landing.feature', { number: index + 1 })}</legend>
            <TextField
              label={t('admin.landing.featureTitle', { language: languageName })}
              lang={language}
              dir="auto"
              maxLength={80}
              value={feature.title[language] ?? ''}
              onChange={(event) => {
                setContent({
                  ...content,
                  features: content.features.map((item, at) =>
                    at === index ? { ...item, title: set(item.title, event.target.value) } : item,
                  ),
                });
              }}
            />
            <TextAreaField
              label={t('admin.landing.featureText', { language: languageName })}
              lang={language}
              dir="auto"
              maxLength={400}
              value={feature.text[language] ?? ''}
              onChange={(event) => {
                setContent({
                  ...content,
                  features: content.features.map((item, at) =>
                    at === index ? { ...item, text: set(item.text, event.target.value) } : item,
                  ),
                });
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                setContent({
                  ...content,
                  features: content.features.filter((_, at) => at !== index),
                });
              }}
            >
              {t('admin.landing.remove')}
            </Button>
          </fieldset>
        ))}
        {content.features.length < 12 ? (
          <Button
            variant="secondary"
            onClick={() => {
              setContent({ ...content, features: [...content.features, { title: {}, text: {} }] });
            }}
          >
            {t('admin.landing.addFeature')}
          </Button>
        ) : null}
      </Card>

      <Card labelledBy="landing-faq">
        <h2 id="landing-faq" className="mrigl-subheading">
          {t('admin.landing.faq')}
        </h2>
        {content.faq.map((entry, index) => (
          <fieldset key={`faq-${String(index)}`} className="mrigl-stack admin-landing__item">
            <legend>{t('admin.landing.question', { number: index + 1 })}</legend>
            <TextField
              label={t('admin.landing.questionText', { language: languageName })}
              lang={language}
              dir="auto"
              maxLength={200}
              value={entry.question[language] ?? ''}
              onChange={(event) => {
                setContent({
                  ...content,
                  faq: content.faq.map((item, at) =>
                    at === index
                      ? { ...item, question: set(item.question, event.target.value) }
                      : item,
                  ),
                });
              }}
            />
            <TextAreaField
              label={t('admin.landing.answer', { language: languageName })}
              lang={language}
              dir="auto"
              maxLength={2000}
              value={entry.answer[language] ?? ''}
              onChange={(event) => {
                setContent({
                  ...content,
                  faq: content.faq.map((item, at) =>
                    at === index ? { ...item, answer: set(item.answer, event.target.value) } : item,
                  ),
                });
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                setContent({ ...content, faq: content.faq.filter((_, at) => at !== index) });
              }}
            >
              {t('admin.landing.remove')}
            </Button>
          </fieldset>
        ))}
        {content.faq.length < 30 ? (
          <Button
            variant="secondary"
            onClick={() => {
              setContent({ ...content, faq: [...content.faq, { question: {}, answer: {} }] });
            }}
          >
            {t('admin.landing.addQuestion')}
          </Button>
        ) : null}
      </Card>

      <Card labelledBy="landing-links">
        <h2 id="landing-links" className="mrigl-subheading">
          {t('admin.landing.links')}
        </h2>
        <TextField
          label={t('admin.landing.terms')}
          hint={t('admin.landing.linkHint')}
          type="url"
          ltr
          value={content.links.terms ?? ''}
          onChange={(event) => {
            const value = event.target.value.trim();
            setContent({
              ...content,
              links: { ...content.links, terms: value === '' ? null : value },
            });
          }}
        />
        <TextField
          label={t('admin.landing.privacy')}
          hint={t('admin.landing.linkHint')}
          type="url"
          ltr
          value={content.links.privacy ?? ''}
          onChange={(event) => {
            const value = event.target.value.trim();
            setContent({
              ...content,
              links: { ...content.links, privacy: value === '' ? null : value },
            });
          }}
        />
      </Card>
      <Button type="submit" busy={busy}>
        {t('admin.save')}
      </Button>
    </form>
  );
}
